import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Download, Shield, LogOut, Mail, ListChecks } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore.js';
import { useThemeStore } from '../../stores/themeStore.js';
import { useSyncStore } from '../../stores/syncStore.js';
import { useUnseenEdits } from '../../hooks/useUnseenEdits.js';
import { Dialog } from '../ui/Dialog.js';
import { Button } from '../ui/Button.js';
import { Avatar } from '../ui/Avatar.js';
import { Badge } from '../ui/Badge.js';

export interface AccountMenuProps {
  open: boolean;
  onClose: () => void;
  canInstallPwa?: boolean;
  onInstallPwa?: () => void;
}

export const AccountMenu: React.FC<AccountMenuProps> = ({
  open,
  onClose,
  canInstallPwa = false,
  onInstallPwa
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isGuest, logout, openChangeEmailDialog } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { deferredPrompt, setDeferredPrompt } = useSyncStore();
  const isPwaInstallable = canInstallPwa || Boolean(deferredPrompt);

  const handlePwaInstall = async () => {
    if (onInstallPwa) {
      onInstallPwa();
    } else if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {
        // ignore
      } finally {
        setDeferredPrompt(null);
      }
    }
    onClose();
  };
  const hasUnseen = useUnseenEdits();
  const isAdmin = user?.role === 'ADMIN';

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/auth');
  };

  const handleAdminClick = () => {
    onClose();
    navigate('/admin/moderation');
  };

  const displayName = isGuest ? 'کاربر مهمان' : user?.fullName || 'کاربر مهمان';
  const displayEmail = isGuest ? 'حالت آفلاین (بدون حساب)' : user?.email || 'حالت آفلاین (بدون حساب)';

  return (
    <Dialog open={open} onClose={onClose} title="حساب کاربری و تنظیمات" size="sm">
      <div data-testid="account-menu-dialog" className="flex flex-col gap-6">
        {/* User Profile Card */}
        <div className="flex items-center gap-4 p-4 rounded-2xl bg-base-100 border border-base-300">
          <div className="relative">
            <Avatar name={displayName} size="lg" />
            {hasUnseen && (
              <span
                data-testid="account-unseen-dot"
                className="absolute top-0 end-0 size-2.5 rounded-full bg-primary ring-2 ring-base-100"
                aria-hidden="true"
              />
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-base text-base-content truncate">
                {displayName}
              </span>
              {isGuest && <Badge tone="info">مهمان</Badge>}
              {isAdmin && <Badge tone="accent">مدیر</Badge>}
            </div>
            <span className="text-xs text-base-content/60 truncate">
              {displayEmail}
            </span>
          </div>
        </div>

        {/* Action Items List */}
        <div className="flex flex-col gap-2">
          {/* My Edits entry (C-36) */}
          {isAuthenticated && !isGuest && (
            <button
              type="button"
              data-testid="account-menu-my-edits"
              onClick={() => {
                onClose();
                navigate('/my-edits');
              }}
              className="flex items-center justify-between p-3.5 rounded-xl bg-base-100 border border-base-300 hover:border-base-content/30 transition text-sm font-medium text-base-content cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <ListChecks className="size-5 text-primary-soft" />
                <span>پیشنهادهای من</span>
              </span>
              {hasUnseen && (
                <span
                  data-testid="account-my-edits-dot"
                  className="size-2 rounded-full bg-primary inline-block"
                  aria-label="تصمیم جدید"
                >
                  <span className="sr-only">تصمیم جدید</span>
                </span>
              )}
            </button>
          )}

          {/* Theme Toggle Button */}
          <button
            type="button"
            data-testid="theme-toggle-button"
            onClick={toggleTheme}
            aria-label="تغییر تم"
            className="flex items-center justify-between p-3.5 rounded-xl bg-base-100 border border-base-300 hover:border-base-content/30 transition text-sm font-medium text-base-content cursor-pointer"
          >
            <span className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Sun className="size-5 text-accent" />
              ) : (
                <Moon className="size-5 text-primary" />
              )}
              <span>حالت نمایش</span>
            </span>
            <span className="text-xs text-base-content/60 font-semibold">
              {theme === 'dark' ? 'تاریک (Studio)' : 'روشن'}
            </span>
          </button>

          {/* PWA Install */}
          {isPwaInstallable && (
            <button
              type="button"
              data-testid="pwa-install-button"
              onClick={handlePwaInstall}
              className="flex items-center justify-between p-3.5 rounded-xl bg-base-100 border border-base-300 hover:border-base-content/30 transition text-sm font-medium text-base-content cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <Download className="size-5 text-primary-soft" />
                <span>نصب برنامه (PWA)</span>
              </span>
              <Badge tone="info">آفلاین</Badge>
            </button>
          )}

          {/* Change email (signed-in accounts only) */}
          {isAuthenticated && !isGuest && (
            <button
              type="button"
              data-testid="change-email-button"
              onClick={() => {
                onClose();
                openChangeEmailDialog();
              }}
              className="flex items-center justify-between p-3.5 rounded-xl bg-base-100 border border-base-300 hover:border-base-content/30 transition text-sm font-medium text-base-content cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <Mail className="size-5 text-primary-soft" />
                <span>تغییر ایمیل</span>
              </span>
            </button>
          )}

          {/* Admin Moderation Link */}
          {isAdmin && (
            <button
              type="button"
              onClick={handleAdminClick}
              className="flex items-center justify-between p-3.5 rounded-xl bg-base-100 border border-base-300 hover:border-base-content/30 transition text-sm font-medium text-base-content cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <Shield className="size-5 text-accent-soft" />
                <span>پنل بررسی تبلچرها</span>
              </span>
            </button>
          )}
        </div>

        {/* Sign In / Sign Out */}
        <div className="pt-2">
          {isAuthenticated && !isGuest ? (
            <Button
              data-testid="logout-button"
              variant="danger"
              fullWidth
              leadingIcon={<LogOut className="size-4" />}
              onClick={handleLogout}
            >
              خروج از حساب
            </Button>
          ) : isGuest ? (
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                fullWidth
                onClick={() => {
                  onClose();
                  navigate('/auth');
                }}
              >
                ورود یا ساخت حساب
              </Button>
              <Button
                data-testid="logout-button"
                variant="ghost"
                fullWidth
                leadingIcon={<LogOut className="size-4" />}
                onClick={handleLogout}
              >
                خروج از حالت مهمان
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                onClose();
                navigate('/auth');
              }}
            >
              ورود یا ساخت حساب
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

AccountMenu.displayName = 'AccountMenu';
